import { FC, PropsWithChildren, useEffect, useMemo } from 'react';

import { Loader, NotAuthorizedView } from '@gasbi/shared';
import organizationManager from '@gasbi/shared/common/utils/organizationManager';
import { appEventBus } from '@gasbi/shared/web/utils/appEventBus';
import { AppEvents } from '@gasbi/shared/web/utils/appEventBus/eventMap';

import { useGetAdminOrganizationQuery } from '@/features/adminOrganization/adminOrganizationApi';
import { useLazyGetCurrentUserQuery } from '@/features/auth/authApi';
import { AppAuthProvider } from '@/providers/AppAuthProvider';

// A 401 from GET /api/user is handled by the http interceptor (navigation to /api/login); a 403 means the identity
// signed in fine but has no access to Distribution Admin (no organizations).
export const AuthInitProvider: FC<PropsWithChildren> = ({ children }) => {
  const [fetchCurrentUser, { data: user, error, isSuccess }] =
    useLazyGetCurrentUserQuery();
  // Admin keeps no organization in the cookie session (GSB-8539): every request names the admin organization in the
  // organizationId header, so it is loaded next to the user, before any page asks for organization data.
  const { data: adminOrganization, isLoading: isAdminOrganizationLoading } =
    useGetAdminOrganizationQuery();

  const isForbidden =
    (error as { status?: number } | undefined)?.status === 403;

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  useEffect(() => {
    if (isForbidden) {
      appEventBus.emit(AppEvents.LoadingStateEvent, false);
    }
  }, [isForbidden]);

  const value = useMemo(
    () =>
      user && {
        user,
        logout: () => {
          window.location.assign('/api/logout?returnUrl=/');
        },
      },
    [user],
  );

  if (isForbidden) {
    return <NotAuthorizedView />;
  }

  if (!isSuccess || !value || isAdminOrganizationLoading) {
    return <Loader />;
  }

  // Set while rendering, as MyOrganizationsRoute does, so the pages' first requests already carry it. Without the
  // admin organization (an error, onboarding not done) MyOrganizationsRoute redirects as before.
  if (
    adminOrganization &&
    organizationManager.getState() !== adminOrganization.id
  ) {
    organizationManager.setState(adminOrganization.id);
  }

  return <AppAuthProvider value={value}>{children}</AppAuthProvider>;
};
